import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty } from 'antd';
import { selectors, stateKeys, actions } from '../../../../model';
import { uploadingFieldNames } from '../../../../config/uploadingStep';
import { stepKeys } from '../../../../config/step';
import {
  importTypes,
  itemsToFetchTransforms,
  settingStepFields,
} from '../../../../config/import';
import { checkDataForPreview } from '../../../../lib/checkDataForPreview';
import StepContainer from '../../../ui/StepContainer/StepContainer';
import GoToBtn, { iconsPos } from '../../../ui/GoToBtn/GoToBtn';
import {
  createWorkbook,
  createErrorHeaders,
  createRows,
} from '../../../../lib/file';
import DownloadErrorsFile from '../../../controllers/DownloadErrorsFile/DownloadErrorsFile';
import PreviewTable from './PreviewTable';
import { useSubmit } from '../../../../api/submit';
import Btn from '../../../../../../../../components/btn';
import { useHouseData } from '../../../../api/houseData';
import FetchError from '../../../ui/FetchError/FetchError';
import styles from './PreviewStep.module.scss';

const { selectStateValue, selectUploadingStepValue } = selectors;
const { addPreviewDataAction, stepChangeAction } = actions;

const importTypeSelector = (state) =>
  selectUploadingStepValue(state, uploadingFieldNames.importType);
const houseSelector = (state) =>
  selectUploadingStepValue(state, uploadingFieldNames.house);
const accountTypeSelector = (state) =>
  selectUploadingStepValue(state, uploadingFieldNames.accountType);
const validationResultSelector = (state) =>
  selectStateValue(state, stateKeys.workbookValidationResult);
const previewDataSelector = (state) =>
  selectStateValue(state, stateKeys.previewData);

const createPreviewData = ({ importType, validationResult, data }) => {
  const { valid, invalid } = checkDataForPreview[importType]({
    validationResult,
    data,
  });

  const previewData = { dataSource: valid };

  if (invalid.length) {
    const fields = settingStepFields[importType];
    const headers = createErrorHeaders(fields);
    const rows = createRows({ data: invalid, fields });
    previewData.errorsWorkbook = createWorkbook({ rows, headers });
  }

  return previewData;
};

const Preparation = () => {
  const importType = useSelector(importTypeSelector);
  const house = useSelector(houseSelector);
  const validationResult = useSelector(validationResultSelector);

  const {
    isLoading,
    error,
    result: fetchResult,
  } = useHouseData({
    importType,
    houseId: house?.key,
  });

  const dataPreparationEnabled =
    Boolean(validationResult) && !isLoading && !error;

  const dispatch = useDispatch();

  useEffect(() => {
    if (dataPreparationEnabled) {
      const previewData = createPreviewData({
        importType,
        validationResult,
        data: fetchResult,
      });

      dispatch(addPreviewDataAction(previewData));
    }
  }, [
    dataPreparationEnabled,
    validationResult,
    fetchResult,
    importType,
    dispatch,
  ]);

  return (
    <StepContainer.BoundingWrap grow>
      {isLoading && <StepContainer.Spin spinning />}
      {error ? <FetchError {...error} /> : null}
    </StepContainer.BoundingWrap>
  );
};

const Content = ({ data }) => {
  const { errorsWorkbook, dataSource } = data;

  return (
    <div className={styles.contentContainer}>
      {Boolean(errorsWorkbook) && (
        <StepContainer.BoundingWrap>
          <DownloadErrorsFile workbook={errorsWorkbook}>
            В файле обнаружены записи с ошибками
          </DownloadErrorsFile>
        </StepContainer.BoundingWrap>
      )}

      {Boolean(dataSource?.length) && <PreviewTable dataSource={dataSource} />}

      {!errorsWorkbook && !dataSource?.length && (
        <Empty description="В файле нет записей для предварительного просмотра" />
      )}
    </div>
  );
};

const GoToSettingStep = ({ disabled = false }) => {
  const dispatch = useDispatch();

  const handleClick = () => dispatch(stepChangeAction(stepKeys.setting));

  return (
    <GoToBtn
      iconPos={iconsPos.left}
      text="Шаг 2: настройка полей"
      onClick={handleClick}
      disabled={disabled}
    />
  );
};

const FooterContentWithDataSource = ({ dataSource }) => {
  const house = useSelector(houseSelector);
  const accountTypeId = useSelector(accountTypeSelector);
  const companyId = useSelector(selectors.selectCompanyId);
  const importType = useSelector(importTypeSelector);

  const items = useMemo(
    () => itemsToFetchTransforms[importType](dataSource),
    [importType, dataSource]
  );

  const { mutate, isLoading } = useSubmit();

  return (
    <>
      <GoToSettingStep disabled={isLoading} />
      <Btn
        onClick={() =>
          mutate({
            items,
            houseId: house?.key,
            companyId,
            accountTypeId,
          })
        }
        loading={isLoading}
      >
        Загрузить
      </Btn>
    </>
  );
};

const ContentFactory = () => {
  const storedPreviewData = useSelector(previewDataSelector);
  const importType = useSelector(importTypeSelector);
  const validationResult = useSelector(validationResultSelector);

  const companyAccountsPreviewData = useMemo(() => {
    if (
      importType !== importTypes.personalAccountsByCompanies ||
      !validationResult
    ) {
      return undefined;
    }

    return createPreviewData({ importType, validationResult });
  }, [importType, validationResult]);

  const previewData = storedPreviewData || companyAccountsPreviewData;
  const dataSource = previewData?.dataSource;

  return (
    <>
      {previewData ? <Content data={previewData} /> : <Preparation />}

      <StepContainer.BoundingWrap>
        <StepContainer.Footer>
          {dataSource?.length ? (
            <FooterContentWithDataSource dataSource={dataSource} />
          ) : (
            <GoToSettingStep />
          )}
        </StepContainer.Footer>
      </StepContainer.BoundingWrap>
    </>
  );
};

const PreviewStep = () => {
  return (
    <>
      <StepContainer.Title>
        Шаг 3 из 3: предварительный просмотр
      </StepContainer.Title>
      <ContentFactory />
    </>
  );
};

export default PreviewStep;
